import {
    Component,
    h,
    ComponentInterface,
    Event,
    Prop,
    EventEmitter,
    Host,
    Element,
    State,
    Watch,
    Listen,
} from "@stencil/core";
import { ChevronDown } from "../icons/chevron-down";
import {
    SelectionBehavior,
    HTMLListItem,
} from "../../utils/behaviors/interfaces";
import { SingleSelectionBehavior } from "../../utils/behaviors/single-selection";
import { MultiSelectionBehavior } from "../../utils/behaviors/multi-selection";
import { generateText } from "../../utils/text";
import { watchForAttributeChanges } from "../../utils/watcher";
import { Placement } from "../popover/popover";
import { ListItemValueChange } from "../list-item/list-item";
import { hasSlot } from "../../utils/utils";

const arrowKeys = ["ArrowUp", "ArrowDown"];

export type SelectCompareFn = (currentValue: any, compareValue: any) => boolean;

export type PopoverPlacement = "bottom" | "top";

// reference implementation for generated ids
// https://github.com/ionic-team/ionic-framework/blob/master/core/src/components/input/input.tsx
let id = 0;

/**
 * @slot - The items of the combobox.
 * @slot no-results-message - The content to show if there are no items to select from.
 */
@Component({
    tag: "uic-combobox",
    styleUrl: "combobox.scss",
})
export class ComboBox implements ComponentInterface {
    private helperTextId = `uic-combobox-helper-text-${++id}`;

    @Element() host: HTMLUicComboboxElement;

    @State()
    isOpen = false;
    @State()
    hasFilterResults = true;
    @State()
    hasFocus = false;
    @State()
    inputValue: string;

    private hasValidInputValue = true;
    private selectionBehavior: SelectionBehavior;
    private observer: MutationObserver;
    private popover: HTMLUicPopoverElement;
    private list: HTMLUicListElement;

    /**
     * The text describing the values to choose from.
     */
    @Prop() label: string;

    /**
     * The value of the dropdown.
     */
    @Prop({ mutable: true }) value?: string | Array<string>;

    /**
     * The text to display instead of the selected option's value.
     */
    @Prop({ mutable: true }) selectedText?: string;

    /**
     * The text to show if no value is selected.
     */
    @Prop() placeholder?: string;

    /**
     * If set to `true`, allows multiple selected values.
     */
    @Prop() multiple = false;

    /**
     * If set to `true`, the user cannot interact with the combobox.
     */
    @Prop({ reflect: true }) disabled = false;

    /**
     * Information with low emphasis.
     */
    @Prop() helperText: string;

    /**
     * If set to `true`, the state is considered invalid.
     */
    @Prop() invalid = false;

    /**
     * Information to show if the value is invalid.
     */
    @Prop() invalidText?: string;

    /**
     * Controls if field is marked as requiring some input in case user has
     * not entered anything.
     */
    @Prop() required: boolean = false;

    /**
     * Controls if field is marked as having changed since last save. This is
     * about some visual indicator, only. The actual saving is beyond the scope
     * of this component.
     */
    @Prop() unsaved: boolean = false;

    /**
     * A function used to compare values.
     * Use it to customize results while typing in the input field.
     */
    @Prop() compareWith?: SelectCompareFn;

    /**
     * The initial placement of the popover.
     * Only use this property if you want to force an initial direction,
     * since the popover calculates its placement based on available space.
     */
    @Prop() placement: PopoverPlacement = "bottom";

    /**
     * Controls if embedded input field may present arbitrary value or not.
     *
     * This must be set in case the input field isn't limited to filtering list
     * of options to pick from but meant to offer input of additional values not
     * offered in dropdown list.
     */
    @Prop() arbitraryInput: boolean = false;

    /**
     * If set to `true` the scrollbars are not hidden when not hovering the scroll panel
     */
    @Prop() scrollbarAutoHideDisabled: boolean = false;

    /**
     * Emitted when the value of the dropdown changes.
     */
    @Event({ eventName: "uicChange" }) change: EventEmitter<string | string[]>;

    @Watch("value")
    onValueChanged() {
        const { inputValue: currentInputValue } = this;
        this.syncInputValue(this.value);
        if (currentInputValue !== this.inputValue) {
            this.selectionBehavior.setValue(this.value);
        }
        this.change.emit(this.value);
    }

    @Watch("multiple")
    onMultipleChanged() {
        this.selectionBehavior = this.multiple
            ? this.createMultiSelectionBehavior()
            : this.createSingleSelectionBehavior();

        if (
            this.value === undefined ||
            this.value === null ||
            this.value === ""
        ) {
            return;
        }
        const value = Array.isArray(this.value) ? this.value : [this.value];
        this.value = this.multiple ? value : value[0];
    }

    connectedCallback() {
        this.onMultipleChanged();

        this.observer = watchForAttributeChanges(this.host, "uic-item", () =>
            this.selectionBehavior.setValue(this.value)
        );
    }

    componentDidLoad() {
        this.selectionBehavior.setValue(this.value);
        this.syncInputValue(this.value);
        this.hasFilterResults = this.getItems().length > 0;
    }

    disconnectedCallback() {
        this.observer.disconnect();
        this.observer = null;
    }

    @Listen("uicItemAdded")
    public onListItemAdded(event: CustomEvent<string>): void {
        if (this.matchesValue(event.detail)) {
            this.syncInputValue(this.value);
        }
    }

    @Listen("uicItemUpdated")
    public onListItemUpdated(event: CustomEvent<ListItemValueChange>): void {
        const matchesNow = this.matchesValue(event.detail.current);

        if (matchesNow !== this.matchesValue(event.detail.previous)) {
            this.syncInputValue(this.value);
        }
    }

    /**
     * Tests if provided value equals current value of single-selection list or
     * is included with list of values in a multi-selection list.
     *
     * @param value value to be tested
     * @private
     */
    private matchesValue(value: string): boolean {
        if (value == null) {
            return false;
        }

        if (Array.isArray(this.value)) {
            for (const v of this.value) {
                if (value === v) {
                    return true;
                }
            }

            return false;
        }

        return this.value === value;
    }

    /**
     * Update input field value based on provided combobox value.
     *
     * By default, the input field value is compiled from labels of dropdown
     * items selected by combobox value. As an exemption, any string is accepted
     * as fallback in case `arbitrary-input` is enabled and neither listed item
     * is matching that combobox value.
     *
     * @param value value of combobox
     * @private
     */
    private syncInputValue(value: any) {
        this.hasValidInputValue = true;
        this.inputValue = generateText(this.getItems(), value);

        if (
            this.inputValue.trim() === "" &&
            !this.multiple &&
            this.arbitraryInput
        ) {
            this.filterByInput((Array.isArray(value) ? value[0] : value) || "");
        }
    }

    private createSingleSelectionBehavior() {
        return new SingleSelectionBehavior({
            getItems: this.getItems.bind(this),
            getSelectedItem: this.getSelectedItem.bind(this),
            notifySelect: () => {
                this.hide();
            },
            setValue: (value: string) => {
                this.selectedText = undefined;
                this.syncInputValue(value);
                this.value = value;
            },
        });
    }

    private createMultiSelectionBehavior() {
        return new MultiSelectionBehavior({
            getItems: this.getItems.bind(this),
            getSelectedItem: this.getSelectedItem.bind(this),
            notifySelect: () => {},
            setValue: (value: any) => {
                this.selectedText = undefined;
                this.syncInputValue(value);
                this.value = value;
            },
        });
    }

    private show() {
        this.popover.show();
    }

    private hide() {
        this.popover.hide();
        this.hasFocus = false;
    }

    private handleFilterInput(event: Event) {
        event.stopPropagation();

        const value = (event.target as HTMLUicInputFieldElement).value;

        if (this.inputValue === value || this.selectedText === value) {
            return;
        }

        this.filterByInput(value);
    }

    private filterByInput(value: string): void {
        const items = this.getItems(true);
        const hasSelectedValue =
            typeof value === "string" && value.trim() !== "";
        this.inputValue = value;
        this.hasValidInputValue = hasSelectedValue;

        if (!hasSelectedValue) {
            this.value = "";
            this.showAllListItems(items);
            requestAnimationFrame(() => this.selectionBehavior.clearValue());
        } else {
            this.filterListItems(items, value);
        }
    }

    private showAllListItems(items: HTMLElement[]) {
        items.forEach((item) => {
            item.classList.remove("uic-hidden");
        });
        this.hasFilterResults = items.length > 0;
    }

    private defaultCompareWith(current: string, compare: string) {
        return current
            .replace(/\s+/g, " ")
            .toLocaleLowerCase()
            .includes(compare.replace(/\s+/g, " ").toLocaleLowerCase());
    }

    private filterListItems(items: HTMLListItem[], query: string) {
        const compareFn =
            this.compareWith != null
                ? (item) => this.compareWith(item.value, query)
                : (item) => this.defaultCompareWith(item.textContent, query);
        let filterResultsCount = 0;

        items.forEach((item) => {
            const show = compareFn(item);
            if (show) {
                filterResultsCount++;
                item.classList.remove("uic-hidden");
            } else {
                item.classList.add("uic-hidden");
            }
        });

        this.hasFilterResults = filterResultsCount > 0;
    }

    private handleFilterFocus() {
        this.show();
        this.hasFocus = true;
    }

    private handleInputKeyDown(event: KeyboardEvent) {
        if (arrowKeys.includes(event.key)) {
            const list = this.host.querySelector("uic-list");
            if (list) {
                setTimeout(() => list.setFocus());
            }
        }
    }

    private handleSelect(event: CustomEvent) {
        const target = (event.detail || event.target) as any;

        this.showAllListItems(this.getItems());
        this.selectionBehavior.select(target);
    }

    private handlePopoverShow() {
        // popover of combobox should align with input/button
        // since the list is hosted by the popover, we must enforce dimensions manually
        const padding = 16;
        const width = `${this.host.clientWidth - padding}px`;
        this.list.style.minWidth = width;
        this.list.style.maxWidth = width;
        this.isOpen = true;
    }

    private handlePopoverHide() {
        this.isOpen = false;
    }

    private getSelectedItem() {
        return this.host.querySelector<HTMLElement>("[uic-item][selected]");
    }

    private getItems(includeHidden = false) {
        let query = includeHidden
            ? "[uic-item]"
            : "[uic-item]:not(.uic-hidden)";
        return [...this.host.querySelectorAll<HTMLListItem>(query)];
    }

    render() {
        const hasFilterResults = this.hasFilterResults;
        const isOpen = this.isOpen;
        const value = this.selectedText || this.inputValue;
        const isClearable = this.hasFocus || this.hasValidInputValue;
        const flag = !isClearable ? "error" : null;
        const placement = `${this.placement}-start` as Placement;

        const helperText =
            this.invalid && this.invalidText != null
                ? this.invalidText
                : this.helperText;

        const hasHelperText = !!helperText || hasSlot(this.host, "helper-text");

        return (
            <Host
                class={{
                    "uic-combobox": true,
                    "uic-combobox--no-results": !hasFilterResults,
                    "uic-combobox--open": isOpen,
                    "uic-combobox--disabled": this.disabled,
                    "uic-combobox--invalid": this.invalid,
                    "uic-combobox--has-helper-text": hasHelperText,
                }}
            >
                <uic-popover
                    ref={(el) => (this.popover = el)}
                    containingElement={this.host}
                    onUicShow={() => this.handlePopoverShow()}
                    onUicHide={() => this.handlePopoverHide()}
                    distance={-4}
                    skidding={8}
                    placement={placement}
                >
                    <div slot="trigger" class="uic-combobox__wrapper">
                        <uic-input-field
                            label={this.label}
                            value={value}
                            clearable={isClearable}
                            flag={flag}
                            onKeyUp={(e) => this.handleFilterInput(e)}
                            onKeyDown={(e) => this.handleInputKeyDown(e)}
                            onClick={(e) => {
                                this.show();
                                e.stopPropagation();
                            }}
                            onUicChange={(e) => this.handleFilterInput(e)}
                            onUicFocus={() => this.handleFilterFocus()}
                            placeholder={this.placeholder}
                            disabled={this.disabled}
                            required={this.required}
                            invalid={this.invalid}
                            unsaved={this.unsaved}
                            class="uic-combobox__filter"
                        />
                        <uic-button
                            size="icon-stretched"
                            class="uic-combobox__toggle"
                            disabled={this.disabled}
                        >
                            <span class="uic-combobox__toggle-icon" slot="icon">
                                {ChevronDown()}
                            </span>
                        </uic-button>
                    </div>
                    <uic-list
                        ref={(el) => (this.list = el)}
                        onUicSelect={(e) => this.handleSelect(e)}
                    >
                        <uic-scrollpanel
                            class="uic-combobox__scrollpanel"
                            scrollbarAutoHideDisabled={
                                this.scrollbarAutoHideDisabled
                            }
                        >
                            <div class="uic-combobox__filter-message">
                                <slot name="no-results-message" />
                            </div>
                            <slot />
                        </uic-scrollpanel>
                    </uic-list>
                </uic-popover>

                <div class="uic-combobox__helper-text" id={this.helperTextId}>
                    <slot name="helper-text">
                        {/**  wrapping in div because otherwise, Stencil will fail to place the text inside the slot */}
                        <div class="uic-combobox__helper-text-inner">
                            {helperText}
                        </div>
                    </slot>
                </div>
            </Host>
        );
    }
}
